import { CmmFileType } from '../../../shared/types/cmmConfig.ts';
import { CmmConfig, CustomEquipment } from '../../types/sharedTypeImpl.ts';
import { readCmmMetadata } from './readCmmMetadata.ts';

type CmmConfigMetadata = {
    firstPositionsRow: number;
    hasMultipleParts: boolean;
}

type Result = {
    success: true;
    cmmConfig: CmmConfig;
    cmmConfigMetadata: CmmConfigMetadata;
    cmmEquipment: CustomEquipment;
    warningMessage?: string;
} | {
    success: false;
    errorMessage: string;
}

type ConfigMatch = {
    cmmConfig: CmmConfig;
    metadata: CmmConfigMetadata;
}

type EquipmentOption = ConfigMatch & {
    equipment: CustomEquipment;
    fileEquipmentName: string;
}


export function resolveCmmEquipment(
    dataTable: string[][],
    file: File,
    cmmFileType: CmmFileType,
    cmmConfigs: CmmConfig[],
    cmmEquipments: CustomEquipment[] = [],
): Result {
    const matchingExtension = cmmConfigs.filter(it => it.fileType === cmmFileType);
    const matchingMetadata = findMetadataMatches(dataTable, matchingExtension);

    if (matchingMetadata.length === 0) {
        return {
            success: false,
            errorMessage: `No matching CMM configuration found for file "${file.name}".`,
        };
    }

    const options = getEquipmentOptions(dataTable, matchingMetadata, cmmEquipments);
    if (options.length === 0) {
        return {
            success: false,
            errorMessage: `No equipment refers to a matching CMM configuration for file "${file.name}".`,
        };
    }

    const nameMatch = options.find(equipmentNameMatches);
    if (nameMatch) {
        return {
            success: true,
            cmmConfig: nameMatch.cmmConfig,
            cmmConfigMetadata: nameMatch.metadata,
            cmmEquipment: nameMatch.equipment,
        }
    }

    const selectedOption = options[0];
    if (!selectedOption.equipment) {
        return {
            success: false,
            errorMessage: `No equipment refers to CMM configuration "${selectedOption.cmmConfig.name}".`,
        };
    }

    let warningMessage: string | undefined;
    if (options.length > 1) {
        const targetName = options.find(option => option.fileEquipmentName.length > 0)?.fileEquipmentName;
        const reason = targetName ? 'nameNotFound' : 'missingName';
        warningMessage = getFallbackWarning(reason, selectedOption.equipment.displayName, targetName);
    }
    return {
        success: true,
        cmmConfig: selectedOption.cmmConfig,
        cmmConfigMetadata: selectedOption.metadata,
        cmmEquipment: selectedOption.equipment,
        warningMessage,
    }
}

function findMetadataMatches(
    dataTable: string[][],
    cmmConfigs: CmmConfig[],
): ConfigMatch[] {
    const matchingConfigs: ConfigMatch[] = [];
    for (const cmmConfig of cmmConfigs) {
        const metadataResult = readCmmMetadata(dataTable, cmmConfig);
        if (!metadataResult.success) {
            continue;
        }

        matchingConfigs.push({
            cmmConfig,
            metadata: metadataResult,
        });
    }
    return matchingConfigs;
}

// Config order takes priority over equipment order when choosing a fallback.
function getEquipmentOptions(
    dataTable: string[][],
    matchingConfigs: ConfigMatch[],
    cmmEquipments: CustomEquipment[],
): EquipmentOption[] {
    const options: EquipmentOption[] = [];
    for (const match of matchingConfigs) {
        const equipments = cmmEquipments.filter(it => it.cmmConfigId === match.cmmConfig._id);
        const fileEquipmentName = readEquipmentName(dataTable, match.cmmConfig);
        for (const equipment of equipments) {
            options.push({
                ...match,
                equipment,
                fileEquipmentName,
            });
        }
    }
    return options;
}

function readEquipmentName(
    dataTable: string[][],
    cmmConfig: CmmConfig,
): string {
    if (!cmmConfig.equipmentNameCell) return '';

    const { row, column } = cmmConfig.equipmentNameCell;
    const rawCellValue = dataTable[row]?.[column] ?? '';
    const parsedEquipmentName = rawCellValue.trim();
    return parsedEquipmentName;
}

function equipmentNameMatches(option: EquipmentOption): boolean {
    if (option.fileEquipmentName.length === 0) return false;

    const matches = option.fileEquipmentName.toLocaleLowerCase()
        === option.equipment.displayName.trim().toLocaleLowerCase();
    return matches;
}

function getFallbackWarning(
    reason: 'missingName' | 'nameNotFound',
    selectedName: string,
    targetName: string | undefined,
): string {
    const reasonText = reason === 'nameNotFound'
        ? `Equipment named "${targetName}" not found.`
        : 'Equipment unknown.';
    const warning = `${reasonText} Using ${selectedName}`;
    return warning;
}
